import { debounce, mergeDeep } from '@sitecore-search/common';
import { Actions, OCController, PageController, WidgetDataAdapter } from '@sitecore-search/core';
import { OCContext, Context as WidgetContext } from '@sitecore-search/data';
export const getQueryKey = (instanceId, request, ...rest) => {
    return [
        request.getRfkId(),
        instanceId,
        PageController.getContext().toJson(),
        request.toJson(),
        OCController.getContext().toJson(),
        ...rest,
    ];
};
export class Query {
    constructor(instanceId, widgetRequest) {
        Object.defineProperty(this, "instanceId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "context", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new WidgetContext()
        });
        Object.defineProperty(this, "ocContext", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new OCContext()
        });
        Object.defineProperty(this, "initialized", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "debounced", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "debounceBy", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "widgetRequest", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "lastResponse", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "enabled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "enabledChangeListeners", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "destroyListeners", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        this.widgetRequest = widgetRequest;
        this.instanceId = instanceId;
    }
    onUpdate(listener) {
        const onRequestChangeDebounced = debounce(() => {
            listener();
        }, 50);
        const pageContextPageListener = {
            handleAction(action) {
                switch (action.type) {
                    case Actions.OC_CONTEXT_CHANGED: {
                        onRequestChangeDebounced();
                        break;
                    }
                    case Actions.PAGE_CONTEXT_CHANGED: {
                        onRequestChangeDebounced();
                        break;
                    }
                }
            },
        };
        PageController.register(pageContextPageListener);
        const requestChangeListener = {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onChange(observable, name, value) {
                onRequestChangeDebounced();
            },
        };
        this.getRequest().addListener(requestChangeListener);
        this.getContext().addListener(requestChangeListener);
        this.getOCContext().addListener(requestChangeListener);
        const enabledChangeListener = (enabled) => {
            if (enabled) {
                onRequestChangeDebounced();
            }
        };
        this.enabledChangeListeners.push(enabledChangeListener);
        this.destroyListeners.push(() => {
            PageController.unregister(pageContextPageListener);
            this.getRequest().removeListener(requestChangeListener);
            this.getContext().removeListener(requestChangeListener);
            this.getOCContext().removeListener(requestChangeListener);
            this.enabledChangeListeners = this.enabledChangeListeners.filter((listener) => listener !== enabledChangeListener);
        });
    }
    destroy() {
        this.destroyListeners.forEach((listener) => {
            listener();
        });
        this.destroyListeners = [];
    }
    getContext() {
        return this.context;
    }
    getOCContext() {
        return this.ocContext;
    }
    getRequest() {
        return this.widgetRequest;
    }
    getKey() {
        return getQueryKey(this.instanceId, this.widgetRequest, this.context.toJson());
    }
    getLastResponse() {
        return this.lastResponse;
    }
    getRequestContext() {
        return mergeDeep(PageController.getContext().toJson(), this.context.toJson());
    }
    getRequestOCContext() {
        return mergeDeep(OCController.getContext().toJson(), this.ocContext.toJson());
    }
    setDebounced(debounced) {
        this.debounced = debounced;
    }
    isDebounced() {
        return this.debounced;
    }
    setDebounceBy(...by) {
        this.debounceBy = by.join('#');
    }
    getDebounceBy() {
        return this.debounceBy;
    }
    fetch(options = {}) {
        return this.run(options);
    }
    run(options = {}) {
        const rfkId = this.getRequest().getRfkId();
        const entity = this.getRequest().getEntity();
        const instanceId = this.instanceId;
        const widgetData = {
            rfkId,
            entity,
            instanceId,
            request: this.getRequest(),
            context: this.getContext(),
            ocContext: this.getOCContext(),
        };
        return WidgetDataAdapter.getWidgetData(widgetData, {
            debounceBy: this.getDebounceBy(),
            debounce: this.isDebounced(),
            ...options,
        }).then((widgetResponse) => {
            this.lastResponse = widgetResponse;
            return widgetResponse;
        });
    }
    setEnabled(enabled) {
        if (enabled !== this.enabled) {
            this.enabled = enabled;
            this.enabledChangeListeners.forEach((listener) => {
                listener(enabled);
            });
        }
    }
    toggleEnabled() {
        this.setEnabled(!this.enabled);
    }
    isEnabled() {
        return this.enabled;
    }
    getInstanceId() {
        return this.instanceId;
    }
}
export class SubQuery extends Query {
    constructor(parentQuery, request) {
        super(parentQuery.getInstanceId(), request.load(parentQuery.getRequest()));
    }
    getKey() {
        return [...super.getKey(), { subQuery: true }];
    }
}
